import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Textarea } from '@artosera/ui';
import {
  CATEGORIES_COUT_EXPOSITION,
  DEVISES,
  type CoutExposition,
  type SaisieCoutExposition,
} from '@artosera/shared';
import { usePilotage, useGestePilotage } from '../../api/pilotage.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { EtatPilotage, montantPilotage } from './presentation.js';
import styles from './Pilotage.module.css';

function FormulaireCout({
  expositionId,
  cout,
  fermer,
}: {
  expositionId: string;
  cout: CoutExposition | null;
  fermer: () => void;
}) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const [categorie, setCategorie] = useState<SaisieCoutExposition['categorie']>(
    cout?.categorie ?? 'stand',
  );
  const [libelle, setLibelle] = useState(cout?.libelle ?? '');
  const [montant, setMontant] = useState(cout ? (cout.montantCentimes / 100).toFixed(2) : '');
  const [devise, setDevise] = useState(cout?.devise ?? 'EUR');
  const [date, setDate] = useState(cout?.date ?? new Date().toISOString().slice(0, 10));
  const [fournisseur, setFournisseur] = useState(cout?.fournisseur ?? '');
  const [note, setNote] = useState(cout?.note ?? '');
  const geste = useGestePilotage(galerieSlug, fermer);
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        geste.mutate({
          methode: cout ? 'put' : 'post',
          suite: `/expositions/${expositionId}/couts${cout ? `/${cout.id}` : ''}`,
          corps: {
            categorie,
            libelle,
            montantCentimes: Math.round(Number(montant.replace(',', '.')) * 100),
            devise,
            date,
            fournisseur,
            note,
          },
        });
      }}
    >
      <label>
        {t('pilotage.categorie')}
        <select
          value={categorie}
          onChange={(e) => setCategorie(e.target.value as SaisieCoutExposition['categorie'])}
        >
          {CATEGORIES_COUT_EXPOSITION.map((c) => (
            <option key={c} value={c}>
              {t(`pilotage.categories.${c}`)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t('pilotage.libelle')}
        <Input
          required
          maxLength={200}
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
        />
      </label>
      <label>
        {t('pilotage.montantHt')}
        <Input
          inputMode="decimal"
          type="number"
          min="0.01"
          step="0.01"
          required
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
        />
      </label>
      <label>
        {t('pilotage.devise')}
        <select
          value={devise}
          onChange={(e) => setDevise(e.target.value as SaisieCoutExposition['devise'])}
        >
          {DEVISES.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </select>
      </label>
      <label>
        {t('pilotage.date')}
        <Input type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label>
        {t('pilotage.fournisseur')}
        <Input
          maxLength={200}
          value={fournisseur}
          onChange={(e) => setFournisseur(e.target.value)}
        />
      </label>
      <label>
        {t('pilotage.note')}
        <Textarea maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <EtatPilotage enCours={geste.isPending} erreur={geste.error} />
      <div>
        <Button type="submit" disabled={geste.isPending}>
          {t('pilotage.enregistrer')}
        </Button>{' '}
        <Button type="button" disabled={geste.isPending} onClick={fermer}>
          {t('pilotage.annuler')}
        </Button>
      </div>
    </form>
  );
}
export function CoutsExposition({ expositionId }: { expositionId: string }) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const fiche = usePilotage<{ couts: CoutExposition[] }>(
    galerieSlug,
    `/expositions/${expositionId}`,
  );
  const [edition, setEdition] = useState<CoutExposition | null | false>(false);
  const [suppression, setSuppression] = useState<string | null>(null);
  const supprimer = useGestePilotage(galerieSlug, () => setSuppression(null));
  const peutEcrire = galerieALaction(galerie, 'insights:write');
  return (
    <section>
      <h2>{t('pilotage.couts')}</h2>
      <EtatPilotage enCours={fiche.isPending} erreur={fiche.error} />
      <div className={styles.defilement}>
        <table>
          <thead>
            <tr>
              {['date', 'categorie', 'libelle', 'montantHt', 'fournisseur', 'note'].map((k) => (
                <th key={k}>{t(`pilotage.${k}`)}</th>
              ))}
              {peutEcrire && <th>{t('pilotage.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {fiche.data?.couts.map((c) => (
              <tr key={c.id}>
                <td>{c.date}</td>
                <td>{t(`pilotage.categories.${c.categorie}`)}</td>
                <td>{c.libelle}</td>
                <td>{montantPilotage(c.montantCentimes, c.devise)}</td>
                <td>{c.fournisseur}</td>
                <td>{c.note}</td>
                {peutEcrire && (
                  <td>
                    <Button onClick={() => setEdition(c)}>{t('pilotage.modifier')}</Button>{' '}
                    <Button onClick={() => setSuppression(c.id)}>{t('pilotage.supprimer')}</Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EtatPilotage enCours={supprimer.isPending} erreur={supprimer.error} />
      {suppression && (
        <div role="alert">
          <p>{t('pilotage.confirmerSuppression')}</p>
          <Button
            disabled={supprimer.isPending}
            onClick={() =>
              supprimer.mutate({
                methode: 'delete',
                suite: `/expositions/${expositionId}/couts/${suppression}`,
              })
            }
          >
            {t('pilotage.confirmer')}
          </Button>{' '}
          <Button onClick={() => setSuppression(null)}>{t('pilotage.annuler')}</Button>
        </div>
      )}
      {peutEcrire && edition === false && (
        <Button onClick={() => setEdition(null)}>{t('pilotage.ajouterCout')}</Button>
      )}
      {peutEcrire && edition !== false && (
        <FormulaireCout
          key={edition?.id ?? 'nouveau'}
          expositionId={expositionId}
          cout={edition}
          fermer={() => setEdition(false)}
        />
      )}
    </section>
  );
}
